import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { API_URL } from "../../../helpers/utils";

interface CreatePriceSheetParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createPriceSheetAPI =
  ({ payload, onSuccess, onError }: CreatePriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/price-sheets`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Price Sheet created successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to create Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Price Sheet error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to create Price Sheet";
      message.error(errorMessage);
    }
  };

export interface PriceSheetFilterParams {
  id?: string;
  search?: string;
  is_active?: string;
  is_default?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
  sortBy?: string | null;
  sortOrder?: string | null;
  created_by?: string[];
}

interface GetPriceSheetListParams {
  filters: PriceSheetFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getPriceSheetListAPI =
  ({ filters, onSuccess, onError, signal }: GetPriceSheetListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/price-sheets", {
        params: filters,
        signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Price Sheet List Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to fetch Price Sheet",
      );
      onError?.();
    }
  };

interface ImportPriceSheetParams {
  file: File;
  price_id: string;
  onSuccess: () => void;
  onError: () => void;
}

export const importPriceSheetAPI =
  ({ file, price_id, onSuccess, onError }: ImportPriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const formData = new FormData();

      formData.append("price_id", price_id);
      formData.append("file", file);
      setTimeout(() => {
        onSuccess();
      }, 1000);
      const response = await axiosInstance.post(
        `/price-sheets/import`,
        formData,
        {
          headers: {
            // Essential for file uploads
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // 3. Handle Success
      if (response.data?.success) {
        // message.success(
        //   response.data?.message || "Price Sheet imported successfully!",
        // );
        // onSuccess();
      } else {
        message.error(response.data?.message || "Import failed");
        onError();
      }
    } catch (error: any) {
      console.error("Import error:", error);

      const errorMessage =
        error?.response?.data?.message || "Import failed. Please try again.";
      message.error(errorMessage);
      onError();
    }
  };

interface ExportPriceSheetParams {
  onSuccess?: () => void;
  onError?: () => void;
}

export const exportPriceSheetAPI =
  ({ onSuccess, onError }: ExportPriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/products/export/excel`, {
        responseType: "blob",
      });

      // --- 1. EXTRACT FILENAME FROM HEADERS ---
      let fileName = "PriceSheet.xlsx"; // Default Fallback

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        // Regex to find "filename=" value, handling quotes
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      // 2. Create a "Blob" from the binary data
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();

        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const result: any = await blobToBase64(blob);
          const base64Data = result?.split(",")[1];

          const uniqueFileName = `${Date.now()}_${fileName}`;

          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
          });

          onSuccess?.();
        } catch (err) {
          console.error("Native Share Error:", err);
          onError?.();
        }
      } else {
        // 3. Create a hidden link element to trigger the download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // 4. Set the dynamic filename
        link.setAttribute("download", fileName);

        // 5. Trigger click and cleanup
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        onSuccess?.();
        message.success("File downloaded successfully!");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      onError?.();

      // Helper to read the error message from a Blob
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            message.error(errorObj.message || "Failed to download file");
          } catch (e) {
            message.error("Failed to download file");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        message.error("Failed to download file");
      }
    }
  };

export interface PriceSheetItemsFilterParams {
  search?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
  page?: number;
  limit?: number;
}

interface GetPriceSheetItemsListParams {
  price_id: string;
  filters: PriceSheetItemsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getPriceSheetItemsListAPI =
  ({
    price_id,
    filters,
    onSuccess,
    onError,
    signal,
  }: GetPriceSheetItemsListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/price-sheets/${price_id}/items`,
        {
          params: filters,
          signal,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result);
      } else {
        message.error(result?.message || "Failed to fetch Price Sheet Items");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Price Sheet Items List Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to fetch Price Sheet Items",
      );
      onError?.();
    }
  };

export const fetchPriceSheetItemsQueryFn = async (
  price_id: string,
  filters: any,
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.get(`/price-sheets/${price_id}/items`, {
    params: filters,
    signal,
  });

  const result = responseDataFormate(response.data);

  if (result?.success) {
    return result;
  } else {
    throw new Error(result?.message || "Failed to fetch Price Sheet Items");
  }
};

interface DuplicatePriceSheetParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const duplicatePriceSheetAPI =
  ({ payload, onSuccess, onError }: DuplicatePriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/price-sheets/duplicate`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Price Sheet duplicated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to duplicate Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Duplicate Price Sheet error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to duplicate Price Sheet";
      message.error(errorMessage);
    }
  };

interface MakeDefaultPriceSheetParams {
  price_id: string;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const markDefaultPriceSheetAPI =
  ({ price_id, onSuccess, onError }: MakeDefaultPriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/price-sheets/${price_id}/set-default`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Mark price sheet default error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface UpdatePriceSheetParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updatePriceSheetAPI =
  ({ payload, onSuccess, onError }: UpdatePriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(`/price-sheets`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Price Sheet updated successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Price Sheet error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Price Sheet";
      message.error(errorMessage);
    }
  };

interface DeletePriceSheetParams {
  id: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deletePriceSheetAPI =
  ({ id, onSuccess, onError }: DeletePriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/price-sheets`, {
        data: { id: id },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Price Sheet deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Price Sheet error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Price Sheet";
      message.error(errorMessage);
    }
  };

export interface PriceSheetProductItemsFilterParams {
  product_id: string;
  page?: number;
  limit?: number;
  finish?: string;
  color?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
}

interface GetPriceSheetProductItemsListParams {
  price_id: string;
  filters: PriceSheetProductItemsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getPriceSheetProductItemsListAPI =
  ({
    price_id,
    filters,
    onSuccess,
    onError,
    signal,
  }: GetPriceSheetProductItemsListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/price-sheets/${price_id}/variants`,
        {
          params: filters,
          signal,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result?.data);
      } else {
        message.error(
          result?.message || "Failed to fetch Price Sheet Product Items",
        );
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Price Sheet Product Items List Error:", error);
      message.error(
        error?.response?.data?.message ||
          "Unable to fetch Price Sheet Product Items",
      );
      onError?.();
    }
  };

interface CreateVariantParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createVariantAPI =
  ({ payload, onSuccess, onError }: CreateVariantParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/price-sheets/variants`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Variant created successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to create Variant");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Variant error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to create Variant";
      message.error(errorMessage);
    }
  };

interface DeletePriceSheetProductParams {
  payload: any;
  onSuccess: () => void;
  onError: () => void;
}

export const deletePriceSheetProductAPI =
  ({ payload, onSuccess, onError }: DeletePriceSheetProductParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/price-sheets/price-items`, {
        data: payload,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Price Sheet deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Price Sheet error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Price Sheet";
      message.error(errorMessage);
    }
  };

interface InActivePricesheetItemParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const inActivePricesheetItemAPI =
  ({ payload, onSuccess, onError }: InActivePricesheetItemParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/price-sheets/price-items/${payload?.price_item_id}/toggle-active`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Price Sheet Item updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Price Sheet Item");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Product error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Product";
      message.error(errorMessage);
    }
  };

interface ExportPriceSheetDetailsParams {
  price_id: string;
  product_id?: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export const deletePriceSheetVariantAPI =
  ({ payload, onSuccess, onError }: any) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `${API_URL}/price-sheets/variants`,
        {
          data: payload,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Variant deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Variant");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Variant error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Variant";
      message.error(errorMessage);
    }
  };

export const exportPriceSheetDetailsAPI =
  ({
    price_id,
    product_id,
    onSuccess,
    onError,
  }: ExportPriceSheetDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/price-sheets/${price_id}/export/excel`,
        {
          params: {
            product_id: product_id,
          },
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Export initiated successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to export Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Export Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to export Price Sheet",
      );
      onError?.();
    }
  };

export const updatePriceshhetVariantsAPI =
  ({ variant_id, payload, onSuccess, onError }: any) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `price-sheets/variants`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Product Variant updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Product Variant");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Product Variant error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Product Variant";
      message.error(errorMessage);
    }
  };

interface GetPriceSheetFiltersParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getPriceSheetFiltersAPI =
  ({ onSuccess, onError }: GetPriceSheetFiltersParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/price-sheets/filter-data");

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Price Sheet Filters Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetPriceSheetVariablesParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getPriceSheetVariablesAPI =
  ({ onSuccess, onError }: GetPriceSheetVariablesParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/price-sheets/variables");

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Price Sheet Variables Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

  export const regenerateNewPricesheetAPI =
  ({ payload, onSuccess, onError }: CreatePriceSheetParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/price-sheets/regenerate`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Price Sheet regenerated successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to regenerate Price Sheet");
        onError?.();
      }
    } catch (error: any) {
      console.error("Regenerate Price Sheet error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to regenerate Price Sheet";
      message.error(errorMessage);
    }
  };